import { ErrorMessage } from '@folio/components/module-elements/ErrorMessage';
import {
  DocumentProcessingStatus,
  DocumentViewer,
  type DocumentViewerDocument,
  isDocumentClassificationProcessing,
  parseAIClassificationMetadata,
  useDocumentViewer,
} from '@folio/components/features/document-viewer';
import { useAssertedParams } from '@folio/lib';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
  getKorintErrorStatus,
  keyValueFieldsToObject,
  policyControllerListPolicyDocumentsQueryOptions,
  policyDocumentControllerGetPolicyDocumentConformityQueryOptions,
  policyDocumentControllerGetPolicyDocumentVerdicts,
  policyDocumentControllerGetPolicyDocumentVerdictsQueryOptions,
  type PolicyDocumentControllerGetPolicyDocumentVerdictsQueryResponse,
  useDocumentValidityControllerOverrideDocumentValidity,
  useDocumentExtractionControllerSubmitExtractionFeedback,
  usePolicyDocumentControllerAccept,
  usePolicyDocumentControllerAcceptAll,
  usePolicyDocumentControllerCancel,
  usePolicyDocumentControllerCorrectPolicyDocumentFraudVerdict,
  usePolicyDocumentControllerReject,
  uploadDocumentsOnPolicy,
  usePolicyControllerDeletePolicyDocuments,
  type CorrectPolicyDocumentFraudVerdictInputBody,
  type DocumentValidityRuleDto,
  type SubmitFeedbackBody,
} from 'korint-sdk';
import { useDocumentConformityMap } from '@folio/hooks';
import {
  useDocumentsAutoClassification,
  useKorintConfig,
  getStoredSession,
  useFeatureFlag,
  type AIValidityResult,
} from 'korint-sdk/react';
import { DocumentsUploadPolicyModule as LegacyDocumentsUploadPolicyModule } from './legacy/DocumentsUploadPolicyModule';
import { Badge } from '@folio/components/ui/badge';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircle2Icon,
  PlusIcon,
  RotateCcwIcon,
  UploadIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components';
import {
  DocumentsCollection,
  type DocumentGroup,
} from '../../components/module-elements/Documents/DocumentsCollection';
import { makeAiSizeValidator } from '../../components/forms/file-input/ai-document-size';
import {
  DOCUMENT_AUTHORIZED_FORMATS,
  FileDropzone,
} from '../../components/ui/file-dropzone/file-dropzone';
import { getUploadedDocumentGroups } from '../../components/module-elements/Documents/uploaded-document-groups';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  ConformityAcceptAllControl,
  ConformityReadOnlyBadge,
  ConformityReuploadZone,
  type DocumentConformityState,
} from '../DocumentsListsPolicyModule/DocumentsListsPolicyModule';
import { getCategoryConformitySummary } from '../DocumentsListsPolicyModule/conformity-summary';
import type { DocumentCategoryType } from '@folio/utils/useDocumentUploadMode';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useTranslation } from 'react-i18next';
import { isUnresolvedCategory } from '@folio/utils/document-categories';
import { getMinSignaturePages } from '@folio/utils/getMinSignaturePages';
import {
  toDocumentReview,
  type DocumentReviewFilter,
  type DocumentReviewSummary,
} from './document-review';
import { useDocumentUploadQueue } from './useDocumentUploadQueue';
import { FRAUD_VERDICT_POLL_INTERVAL_MS, isAwaitingFraudVerdict } from './fraud-verdict-polling';
import { DocumentUploadQueue } from './DocumentUploadQueue';
import { DocumentFileSummary } from './DocumentFileSummary';
import { DocumentReviewFilters } from './DocumentReviewFilters';
import { MissingList, ToValidateList } from './DocumentReviewLists';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type DisplayContext = 'policyholder' | 'broker';
export type DocumentsUploadPolicyModuleProps = {
  /**
   * Defines the context in which the module is displayed: 'policyholder' or 'broker'.
   * In the 'broker' context, it allows to delete documents.
   */
  displayContext: DisplayContext;

  /**
   * Delete gate for callers whose right to delete follows a lifecycle, not the audience (the stop
   * funnel). Defaults to the broker context; `readOnly` still wins.
   */
  canDeleteDocuments?: boolean;

  /**
   * Extra actions to display in the module. This is an escape hatch to display components
   * that are not part of the module.
   */
  extraActions?: React.ReactNode;

  /**
   * The function to be called when a document is uploaded.
   */
  onUpload?: () => void;

  /**
   * Whether to use the branch ID.
   */
  useBranchId?: boolean;

  /**
   * Branch to upload to, for callers whose route carries no :branchId (the stop funnel reads it
   * from the draft). Wins over the route param.
   */
  branchId?: string;

  /**
   * The authorized formats for the documents.
   */
  authorizedFormats?: Record<string, string[]>;

  /**
   * The documents lists to display in the module.
   */
  documentsLists: {
    category: string;
    documentType?: string;
    required?: boolean;
    validity?: DocumentValidityRuleDto[];
  }[];

  /**
   * Category type for determining upload mode. If not specified, falls back to global uploadMode.
   * @default undefined (uses global uploadMode)
   */
  categoryType?: DocumentCategoryType;

  /** Review-only: no dropzone and no delete, for a validator acting on what was provided. */
  readOnly?: boolean;

  /** Hides the summary/accept-all button for callers with an equivalent control elsewhere.
   * @default false
   */
  hideAcceptAllButton?: boolean;

  /** Uses the review filters, concise category labels, and tree hierarchy. */
  reviewLayout?: boolean;

  conformityByDocumentId?: Map<string, DocumentConformityState>;
  onReuploadDocumentConformity?: (documentId: string, file: File) => void;
};

function NoResultsRow() {
  const { t } = useTranslation();
  return <ErrorMessage>{t('documentsUploadPolicy.noResults')}</ErrorMessage>;
}

type TimedOutDoc = Pick<DocumentViewerDocument, 'type' | 'category'> & {
  id: string;
  name: string;
  signedUrl?: string;
  createdAt: string;
  metadata: Record<string, unknown>;
};

function TimedOutDocsSection({
  docs,
  categories,
  onRetryAll,
  isRetrying,
  onView,
  onDelete,
  isBroker,
}: {
  docs: TimedOutDoc[];
  categories: { category: string; required?: boolean }[];
  onRetryAll: () => void;
  isRetrying: boolean;
  onView?: (doc: TimedOutDoc) => void;
  onDelete?: (doc: TimedOutDoc) => Promise<unknown>;
  isBroker: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="mb-4">
      <Group justify="between" gap={2} className="w-full px-3 py-3">
        <Text size="sm" tone="muted" className="min-w-0 flex-1">
          {t('documentReview.unclassifiedHint')}
        </Text>
        <Group className="flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isRetrying}
            onClick={onRetryAll}
          >
            <RotateCcwIcon className="h-3.5 w-3.5 mr-1.5" />
            {t('aiClassification.retryAll')}
          </Button>
        </Group>
      </Group>
      <DocumentsCollection
        groups={[
          { id: 'unclassified', label: t('fileInput.uncategorizedFolderTitle'), documents: docs },
        ]}
        onView={onView}
        onDelete={onDelete}
        classificationCategories={isBroker ? categories : undefined}
        showGroupHeaders={false}
      />
    </div>
  );
}

type UploadOptions = {
  onDrop: (files: File[]) => void | Promise<unknown>;
  multiple: boolean;
  minPages?: number;
  isUploading: boolean;
  authorizedFormats?: Record<string, string[]>;
  enforceAiSizeLimit?: boolean;
};

function UploadButton({
  upload,
  label,
  variant,
  icon,
}: {
  upload: UploadOptions;
  label: string;
  variant: 'outline' | 'ghost';
  icon: ReactNode;
}) {
  const { t } = useTranslation();
  const validator = useMemo(
    () => (upload.enforceAiSizeLimit ? makeAiSizeValidator(t) : undefined),
    [upload.enforceAiSizeLimit, t],
  );
  return (
    <FileDropzone
      onDrop={async files => {
        await upload.onDrop(files);
      }}
      isPending={upload.isUploading}
      multiple={upload.multiple}
      minPages={upload.minPages}
      accept={upload.authorizedFormats ?? DOCUMENT_AUTHORIZED_FORMATS}
      validator={validator}
    >
      <Button
        type="button"
        variant={variant}
        size="sm"
        disabled={upload.isUploading}
        className="gap-1.5"
      >
        {icon}
        {label}
      </Button>
    </FileDropzone>
  );
}

const hasProcessingVerdict = (
  data?: PolicyDocumentControllerGetPolicyDocumentVerdictsQueryResponse,
) => data?.verdicts.some(({ status }) => status === 'PROCESSING') ?? false;

function DocumentsUploadPolicyModuleV2({
  displayContext,
  canDeleteDocuments,
  extraActions,
  onUpload,
  useBranchId,
  branchId: branchIdProp,
  authorizedFormats,
  documentsLists,
  categoryType = 'policyholderDocuments',
  readOnly = false,
  hideAcceptAllButton = false,
  reviewLayout = false,
  conformityByDocumentId: providedConformityByDocumentId,
  onReuploadDocumentConformity,
}: DocumentsUploadPolicyModuleProps) {
  const { t } = useTranslation();
  const isNonDelegatedDocuments = categoryType === 'nonDelegatedDocuments';
  const title = t([
    `documentsUploadPolicy.${displayContext}.title`,
    `documentsUploadPolicy.${categoryType}.title`,
    'documentsUploadPolicy.title',
  ]);
  const subtitle = t(
    [
      `documentsUploadPolicy.${displayContext}.${categoryType}.subtitle`,
      `documentsUploadPolicy.${displayContext}.subtitle`,
      'documentsUploadPolicy.subtitle',
    ],
    '',
  );
  const hasReviewLayout = displayContext === 'broker' || reviewLayout;
  const usesReviewDesign = displayContext === 'broker' && !isNonDelegatedDocuments;
  const cardTitle = hasReviewLayout && !isNonDelegatedDocuments ? t('documentReview.title') : title;
  const { policyId, branchId: branchIdParam } = useAssertedParams<{
    policyId: string;
    branchId?: string;
  }>();

  // Always use the batch/funnel-style rendering on the dashboard.
  const uploadMode = 'batch' as const;

  const sortedDocumentsLists = useMemo(
    () => [...documentsLists].sort((a, b) => (b.required ? 1 : 0) - (a.required ? 1 : 0)),
    [documentsLists],
  );

  const uploadType = isNonDelegatedDocuments ? 'non_delegated' : 'supporting_document';
  const classificationConfigKey =
    categoryType === 'policyholderDocuments' ? 'policyholderDashboard' : categoryType;
  const branchId = branchIdProp ?? (useBranchId ? branchIdParam : undefined);

  const { clientOptions, korintConfig } = useKorintConfig();
  const aiClassificationDisplayConfig =
    korintConfig.modulesConfig?.aiClassification?.interfaces?.[classificationConfigKey];
  // Only used by single-tenant apps, where tenant is always set.
  const getAccessToken = useCallback(
    () => getStoredSession(clientOptions.tenant ?? '')?.accessToken,
    [clientOptions.tenant],
  );

  const queryOptions = policyControllerListPolicyDocumentsQueryOptions(policyId, { branchId });

  const {
    data: allDocuments,
    isLoading: isLoadingDocuments,
    isError: documentsError,
  } = useQuery({
    ...queryOptions,
    select: data =>
      data.data.map(document => ({
        ...document,
        metadata: keyValueFieldsToObject(document.metadata),
      })),
  });

  const managedDocumentTypes = useMemo(
    () =>
      new Set(
        documentsLists
          .map(doc => doc.documentType)
          .filter(Boolean)
          .map(String),
      ),
    [documentsLists],
  );

  const viewableDocuments = useMemo(() => {
    if (!allDocuments) return [];

    const filtered = allDocuments.filter(document => {
      if (document.type !== uploadType) return false;
      if (
        uploadType === 'non_delegated' &&
        (document.metadata.signed === 'true' || document.metadata.archived === 'true')
      )
        return false;
      return (
        isUnresolvedCategory(document.category) ||
        documentsLists.some(
          configuredDocument => configuredDocument.category === document.category,
        ) ||
        managedDocumentTypes.has(document.category ?? '')
      );
    });

    const groups = new Map<string, typeof filtered>();
    filtered.forEach(doc => {
      const rawCategory = doc.category ? String(doc.category) : 'uncategorized';
      const category = isUnresolvedCategory(rawCategory) ? 'uncategorized' : rawCategory;
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)?.push(doc);
    });

    const sortedGroups = Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'uncategorized') return -1;
      if (b === 'uncategorized') return 1;
      return a.localeCompare(b);
    });

    const sorted = sortedGroups.flatMap(([, docs]) => docs);

    return sorted.map(doc => ({
      id: doc.id,
      name: doc.name,
      signedUrl: doc.signedUrl || '',
      createdAt: doc.createdAt,
      type: doc.type,
      category: doc.category,
      metadata: Object.fromEntries(
        Object.entries(doc.metadata).map(([key, value]) => [
          key,
          value === null || value === undefined
            ? undefined
            : typeof value === 'object'
              ? (value as Record<string, unknown>)
              : String(value),
        ]),
      ) as Record<string, string | number | boolean | Record<string, unknown> | undefined>,
    }));
  }, [allDocuments, documentsLists, uploadType, managedDocumentTypes]);

  const {
    selectedDocument,
    selectDocument: selectViewerDocument,
    closeViewer,
  } = useDocumentViewer(viewableDocuments);
  const [viewerDocumentIds, setViewerDocumentIds] = useState<string[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const invalidateOnClassificationChange = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: policyControllerListPolicyDocumentsQueryOptions(policyId, { branchId }).queryKey,
    });
  }, [queryClient, policyId, branchId]);

  // Base branch's real id is the literal string "BASE" (see PolicyAggregateProjection).
  const conformityBranchId = branchId ?? 'BASE';

  const verdictsQueryKey = policyDocumentControllerGetPolicyDocumentVerdictsQueryOptions(policyId, {
    branchId: conformityBranchId,
  }).queryKey;

  const refreshDocumentAnalysis = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: policyControllerListPolicyDocumentsQueryOptions(policyId, { branchId }).queryKey,
    });
    queryClient.invalidateQueries({ queryKey: verdictsQueryKey });
    queryClient.invalidateQueries({
      queryKey: policyDocumentControllerGetPolicyDocumentConformityQueryOptions(policyId, {
        branchId: conformityBranchId,
      }).queryKey,
    });
  }, [queryClient, policyId, branchId, verdictsQueryKey, conformityBranchId]);

  const {
    processDocuments: processClassificationDocuments,
    isProcessing: isClassifying,
    classificationStates,
  } = useDocumentsAutoClassification({
    getAccessToken,
    onDocumentValidated: invalidateOnClassificationChange,
    // A broker's stream completes only after the fraud check, so refresh once validity lands.
    onDocumentValidityChecked: refreshDocumentAnalysis,
    onAllComplete: refreshDocumentAnalysis,
  });

  // A single configured document uses its preset category before AI classification runs.
  const presetDocumentType =
    documentsLists.length === 1 ? documentsLists[0].documentType : undefined;

  const presetCategory = documentsLists.length === 1 ? documentsLists[0].category : undefined;

  // When a single document type is expected, the type is stamped directly on
  // upload (no AI classification), so dropping several files all land on that
  // one type. Block multi-file drops to prevent duplicate-type documents that
  // would later break signature generation (NO_UNIQUE_DOCUMENT_TYPE).
  const allowMultiple = documentsLists.length !== 1;

  const uploadQueue = useDocumentUploadQueue({
    upload: file =>
      uploadDocumentsOnPolicy({
        policyId,
        branchId,
        payload: {
          file,
          type: uploadType,
          category: presetDocumentType ?? presetCategory ?? 'uncategorized',
          metadata: {},
          name: file.name,
        },
      }),
    onUploaded: documentIds => {
      void processClassificationDocuments(documentIds);
      onUpload?.();
    },
  });

  // Source of truth for "this doc needs manual classification" is the persisted doc
  // metadata, not the in-memory hook state — this way the section survives a refresh
  // even after the SSE connection that timed out is gone.
  const unclassifiedDocs = useMemo<TimedOutDoc[]>(() => {
    // No AI classification for this tenant/product → uploaded docs are never auto-classified,
    // so there is nothing to "retry". Skip the timed-out recovery section (and its
    // "Classify with AI" button) entirely; these docs flow into displayDocuments normally.
    if (displayContext !== 'broker' || !aiClassificationDisplayConfig?.enabled) return [];
    if (!viewableDocuments.length) return [];
    return viewableDocuments
      .filter(d => {
        const scanResult = d.metadata.scanresult ? String(d.metadata.scanresult) : '';
        if (scanResult !== 'CLEAN' && scanResult !== 'UNSUPPORTED') return false;
        const cat = d.category ? String(d.category) : '';
        if (!isUnresolvedCategory(cat)) return false;
        const state = classificationStates.get(d.id);
        if (isDocumentClassificationProcessing(state)) return false;
        if (state) {
          // SSE just landed on a real category — hide until the documents query
          // refetch reflects it, otherwise the yellow "À classer manuellement"
          // panel flashes for the window between SSE completion and React
          // Query's invalidation cascade.
          if (
            state.status === 'completed' &&
            state.result?.category &&
            !isUnresolvedCategory(state.result.category)
          ) {
            return false;
          }
        }
        return true;
      })
      .map(
        (d): TimedOutDoc => ({
          id: d.id,
          name: d.name,
          signedUrl: d.signedUrl ?? undefined,
          createdAt: d.createdAt,
          type: d.type,
          category: d.category,
          metadata: d.metadata,
        }),
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [viewableDocuments, classificationStates, aiClassificationDisplayConfig, displayContext]);

  const unclassifiedDocumentIds = useMemo(
    () => unclassifiedDocs.map(d => d.id),
    [unclassifiedDocs],
  );

  // Documents shown in the funnel-style list — user uploads that are already classified
  // (or being classified). Timed-out unclassified docs render in their own dedicated
  // section above (TimedOutDocsSection) to keep the recovery action separate.
  const displayDocuments = useMemo(() => {
    const unclassifiedSet = new Set(unclassifiedDocumentIds);
    return viewableDocuments.filter(d => !unclassifiedSet.has(d.id));
  }, [viewableDocuments, unclassifiedDocumentIds]);

  const conformityCategoryByDocumentId = useMemo(
    () =>
      new Map(
        getUploadedDocumentGroups({
          documents: displayDocuments,
          categories: sortedDocumentsLists,
          translate: t,
        }).flatMap(({ id: category, documents }) =>
          category === 'uncategorized'
            ? []
            : documents.map(({ id: documentId }) => [documentId, category] as const),
        ),
      ),
    [displayDocuments, sortedDocumentsLists, t],
  );

  const handleRetryTimedOutClassifications = useCallback(() => {
    if (unclassifiedDocumentIds.length > 0) {
      processClassificationDocuments(unclassifiedDocumentIds, { force: true });
    }
  }, [processClassificationDocuments, unclassifiedDocumentIds]);

  const { policy } = usePolicyOnDashboard();

  const { data: publicConfig } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
  });
  const { data: publicTenantConfig } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    enabled: !!publicConfig && !publicConfig.ai,
  });
  const verdictAi = publicConfig?.ai ?? publicTenantConfig?.ai;
  const isManualVerdict =
    (verdictAi !== undefined || publicTenantConfig !== undefined) &&
    verdictAi?.documentVerdict !== 'automatic';

  // For a single expected document type (non-delegated upload), require the
  // dropped PDF to have at least as many pages as the highest fixed signature
  // anchor page, so the signature can't land on a missing page. Derived from
  // the signature config — `page: 'LATEST'`/tag-based docs impose no minimum.
  const minPages = getMinSignaturePages(publicConfig?.signature, presetDocumentType);

  const { mutateAsync: submitFeedback, isPending: isFeedbackPending } =
    useDocumentExtractionControllerSubmitExtractionFeedback({
      mutation: {
        onSuccess: () => {
          toast({ description: t('documents.extraction.feedback.submitted') });
        },
        onError: () => {
          toast({
            description: t('documents.extraction.feedback.error'),
            variant: 'destructive',
          });
        },
      },
    });

  const handleExtractionFeedback = useCallback(
    (feedback: SubmitFeedbackBody) => {
      if (!selectedDocument) return;
      return submitFeedback({ documentId: selectedDocument.id, data: feedback }).then(
        () => undefined,
      );
    },
    [selectedDocument, submitFeedback],
  );

  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: [
      'OVERRIDE_DOCUMENT_VALIDITY',
      'REVIEW_POLICY_DOCUMENT',
      'GET_POLICY_DOCUMENT_FRAUD',
      'REVIEW_POLICY_DOCUMENT_FRAUD',
    ],
  });
  const canOverrideValidity = can.OVERRIDE_DOCUMENT_VALIDITY;
  const canDecideConformity = displayContext === 'broker' && can.REVIEW_POLICY_DOCUMENT;
  const canViewFraud = displayContext === 'broker' && can.GET_POLICY_DOCUMENT_FRAUD;
  const canReviewFraud = canViewFraud && can.REVIEW_POLICY_DOCUMENT_FRAUD && !readOnly;

  const {
    data: verdicts,
    isLoading: isLoadingVerdicts,
    isError: verdictsError,
  } = useQuery({
    ...policyDocumentControllerGetPolicyDocumentVerdictsQueryOptions(policyId, {
      branchId: conformityBranchId,
    }),
    queryFn: async ({ signal }) => {
      const wasProcessing = hasProcessingVerdict(queryClient.getQueryData(verdictsQueryKey));
      const data = await policyDocumentControllerGetPolicyDocumentVerdicts(
        policyId,
        { branchId: conformityBranchId },
        { signal },
      );
      if (wasProcessing && !hasProcessingVerdict(data)) {
        void queryClient.invalidateQueries({
          queryKey: policyDocumentControllerGetPolicyDocumentConformityQueryOptions(policyId, {
            branchId: conformityBranchId,
          }).queryKey,
        });
      }
      return data;
    },
    enabled: canDecideConformity && (!useBranchId || !!branchId),
    // A refresh or dropped stream loses the live verdict; scoring keeps running server-side.
    refetchInterval: query => {
      if (hasProcessingVerdict(query.state.data)) return 2000;
      return canViewFraud &&
        isAwaitingFraudVerdict({
          documents: viewableDocuments,
          scores: (query.state.data?.verdicts ?? []).flatMap(({ fraud }) => (fraud ? [fraud] : [])),
          now: Date.now(),
        })
        ? FRAUD_VERDICT_POLL_INTERVAL_MS
        : false;
    },
  });

  const { mutate: correctFraudVerdict, isPending: isCorrectingFraudVerdict } =
    usePolicyDocumentControllerCorrectPolicyDocumentFraudVerdict({
      mutation: {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: verdictsQueryKey }),
        onError: error => {
          if (getKorintErrorStatus(error) === 409)
            void queryClient.invalidateQueries({ queryKey: verdictsQueryKey });
        },
      },
    });
  const handleCorrectFraudVerdict = useCallback(
    (documentId: string, correction: CorrectPolicyDocumentFraudVerdictInputBody) =>
      correctFraudVerdict({ policyId, documentId, data: correction }),
    [correctFraudVerdict, policyId],
  );

  const verdictByDocumentId = useMemo(
    () =>
      new Map(
        (canDecideConformity ? (verdicts?.verdicts ?? []) : []).map(verdict => [
          verdict.documentId,
          verdict,
        ]),
      ),
    [canDecideConformity, verdicts],
  );

  const selectedVerdict = selectedDocument
    ? verdictByDocumentId.get(selectedDocument.id)
    : undefined;
  const selectedDocumentStream = selectedDocument
    ? classificationStates.get(selectedDocument.id)
    : undefined;
  const selectedDocumentFraud = selectedVerdict?.fraud ?? selectedDocumentStream?.fraud;

  const { mutate: overrideValidity, isPending: isValidityOverriding } =
    useDocumentValidityControllerOverrideDocumentValidity({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: verdictsQueryKey });
          toast({ description: t('documents.validity.overrideSuccess') });
        },
        onError: () => {
          toast({
            description: t('documents.validity.overrideError'),
            variant: 'destructive',
          });
        },
      },
    });

  const handleValidityOverride = useCallback(
    (documentId: string, newStatus: 'VALID' | 'INVALID', reason?: string) => {
      overrideValidity({ policyId, documentId, data: { status: newStatus, reason } });
    },
    [overrideValidity, policyId],
  );

  const { mutateAsync: deleteDocument } = usePolicyControllerDeletePolicyDocuments({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      },
      onError: () => {
        toast({
          description: t('documentsUploadPolicy.deleteError'),
          variant: 'destructive',
        });
      },
    },
  });

  // One condition for every delete surface: a reviewer must not be able to remove the proof they
  // are judging, whichever one they reach it through.
  const canDelete = (canDeleteDocuments ?? displayContext === 'broker') && !readOnly;

  const handleDeleteDocument = canDelete
    ? async (document: DocumentViewerDocument) => {
        await deleteDocument({
          policyId,
          params: { ids: [document.id], branchId },
        });
      }
    : undefined;

  const documentConformityFlagEnabled = useFeatureFlag(
    'feat_document_conformity',
    policy.productId,
  );

  const canReviewConformity = displayContext === 'broker' && documentConformityFlagEnabled;
  const {
    data: conformityData,
    isSuccess: isConformityLoaded,
    refetch: refetchConformity,
  } = useQuery({
    ...policyDocumentControllerGetPolicyDocumentConformityQueryOptions(policyId, {
      branchId: conformityBranchId,
    }),
    retry: false,
    enabled: canReviewConformity,
  });
  // A disabled query still serves another caller's cached data with isSuccess: true.
  const isDocumentReviewEnabled = canReviewConformity && isConformityLoaded;

  const { conformityByDocumentId, conformityIdByDocumentId } = useDocumentConformityMap(
    allDocuments,
    conformityData?.conformities,
    isDocumentReviewEnabled,
  );
  const categoryConformitySummary = useMemo(
    () =>
      categoryType === 'policyholderDocuments'
        ? getCategoryConformitySummary({
            categoryByDocumentId: conformityCategoryByDocumentId,
            conformityByDocumentId,
          })
        : undefined,
    [categoryType, conformityByDocumentId, conformityCategoryByDocumentId],
  );
  const visibleConformityByDocumentId = providedConformityByDocumentId ?? conformityByDocumentId;

  const refreshDecisions = useCallback(() => {
    refetchConformity();
    queryClient.invalidateQueries({ queryKey: verdictsQueryKey });
  }, [refetchConformity, queryClient, verdictsQueryKey]);

  const { mutate: acceptDocumentConformityMutation } = usePolicyDocumentControllerAccept({
    mutation: { onSuccess: refreshDecisions },
  });
  const { mutate: rejectDocumentConformityMutation } = usePolicyDocumentControllerReject({
    mutation: { onSuccess: refreshDecisions },
  });
  const { mutate: cancelDocumentConformityMutation } = usePolicyDocumentControllerCancel({
    mutation: { onSuccess: refreshDecisions },
  });
  const { mutate: acceptAllDocumentConformityMutation } = usePolicyDocumentControllerAcceptAll({
    mutation: {
      onSuccess: refreshDecisions,
      onError: () => {
        toast({
          title: t('documentsListsPolicy.conformity.acceptAllErrorTitle'),
          description: t('documentsListsPolicy.conformity.acceptAllError'),
          variant: 'destructive',
        });
      },
    },
  });

  const onAcceptDocumentConformity = useCallback(
    (documentId: string) =>
      acceptDocumentConformityMutation({
        policyId,
        documentId,
        data: { branchId: conformityBranchId },
      }),
    [acceptDocumentConformityMutation, policyId, conformityBranchId],
  );
  const onRejectDocumentConformity = useCallback(
    (documentId: string, reason: string) =>
      rejectDocumentConformityMutation({
        policyId,
        documentId,
        data: { branchId: conformityBranchId, reason },
      }),
    [rejectDocumentConformityMutation, policyId, conformityBranchId],
  );
  const onCancelDocumentConformity = useCallback(
    (documentId: string) => {
      const conformityId = conformityIdByDocumentId.get(documentId);
      if (conformityId) cancelDocumentConformityMutation({ policyId, conformityId });
    },
    [cancelDocumentConformityMutation, conformityIdByDocumentId, policyId],
  );
  const conformToAccept = verdicts?.verdicts.length ? verdicts.conformToAccept : undefined;
  const onAcceptAllDocumentConformity = useCallback(
    () =>
      acceptAllDocumentConformityMutation({
        policyId,
        data: { branchId: conformityBranchId, onlyConform: conformToAccept !== undefined },
      }),
    [acceptAllDocumentConformityMutation, policyId, conformityBranchId, conformToAccept],
  );

  const [selectedReviewFilter, setReviewFilter] = useState<DocumentReviewFilter>('all');
  // The unclassified tab disappears once its last file is classified.
  const reviewFilter =
    selectedReviewFilter === 'unclassified' && unclassifiedDocs.length === 0
      ? 'all'
      : selectedReviewFilter;
  const reviewByDocumentId = new Map(
    viewableDocuments.flatMap(document => {
      const review = toDocumentReview({
        verdict: verdictByDocumentId.get(document.id),
        isUploading:
          document.id.startsWith('optimistic-') ||
          isDocumentClassificationProcessing(classificationStates.get(document.id)),
      });
      return review ? [[document.id, review] as const] : [];
    }),
  );
  const needsAttention = (documentId: string) => {
    const decision = visibleConformityByDocumentId.get(documentId)?.decision;
    return decision !== 'ACCEPTED' && decision !== 'REJECTED';
  };
  const selectedReview = selectedDocument ? reviewByDocumentId.get(selectedDocument.id) : undefined;
  const isSelectedDecisionPending =
    canDecideConformity &&
    !!selectedDocument &&
    visibleConformityByDocumentId.get(selectedDocument.id)?.decision === 'PENDING';
  const requiredCategories = sortedDocumentsLists.filter(({ required }) => required);
  const decisionOf = (documentId: string) =>
    visibleConformityByDocumentId.get(documentId)?.decision;
  const missingRequiredCategories = requiredCategories.filter(({ category, documentType }) =>
    viewableDocuments
      .filter(
        document =>
          document.category === category || (documentType && document.category === documentType),
      )
      .every(({ id }) => decisionOf(id) === 'REJECTED'),
  );
  const undecidedDocuments = displayDocuments.filter(({ id }) => needsAttention(id));
  const conformToValidate = undecidedDocuments.filter(
    ({ id }) => reviewByDocumentId.get(id)?.status === 'checked',
  );
  const flaggedToValidate = undecidedDocuments.filter(({ id }) => {
    const status = reviewByDocumentId.get(id)?.status;
    return status !== 'checked' && status !== 'processing';
  });
  const categoryStatus = (documents: readonly { id: string }[]) => {
    if (!documents.length) return t('documentReview.categoryStatus.noDocument');
    const decisions = documents.map(({ id }) => decisionOf(id));
    if (decisions.includes('ACCEPTED')) return t('documentReview.categoryStatus.validated');
    if (decisions.every(decision => decision === 'REJECTED')) {
      return t('documentReview.categoryStatus.rejected');
    }
    return t('documentReview.categoryStatus.toValidate');
  };
  const filteredCategories =
    reviewFilter === 'missing'
      ? missingRequiredCategories
      : reviewFilter === 'unclassified'
        ? []
        : sortedDocumentsLists;
  const reviewSummary: DocumentReviewSummary = {
    receivedCount: displayDocuments.length,
    missingRequiredCount: missingRequiredCategories.length,
    attentionCount: usesReviewDesign
      ? flaggedToValidate.length + conformToValidate.length
      : undecidedDocuments.length,
    unclassifiedCount: unclassifiedDocs.length,
    processingCount:
      [...reviewByDocumentId.values()].filter(({ status }) => status === 'processing').length +
      uploadQueue.attempts.filter(
        attempt =>
          attempt.status === 'uploading' ||
          (attempt.status === 'uploaded' &&
            !viewableDocuments.some(({ id }) => id === attempt.documentId)),
      ).length,
    isLoading: isLoadingDocuments || isLoadingVerdicts,
    hasError: documentsError || verdictsError,
  };
  const filteredDocuments =
    reviewFilter === 'attention'
      ? displayDocuments.filter(({ id }) => needsAttention(id))
      : reviewFilter === 'all'
        ? displayDocuments
        : [];
  const showUnclassifiedDocuments = !hasReviewLayout || reviewFilter === 'unclassified';
  const emptyCategories = isNonDelegatedDocuments
    ? 'all'
    : hasReviewLayout
      ? reviewFilter === 'attention' || reviewFilter === 'unclassified'
        ? 'hidden'
        : 'required'
      : 'all';

  const viewerDocuments = viewerDocumentIds
    .map(documentId => viewableDocuments.find(({ id }) => id === documentId))
    .filter((document): document is DocumentViewerDocument => document !== undefined);
  const openDocumentViewer = useCallback(
    (document: DocumentViewerDocument) => {
      const documentsForViewer =
        reviewFilter === 'attention'
          ? usesReviewDesign
            ? [...flaggedToValidate, ...conformToValidate]
            : displayDocuments.filter(({ id }) => needsAttention(id))
          : reviewFilter === 'unclassified'
            ? viewableDocuments.filter(({ id }) => unclassifiedDocumentIds.includes(id))
            : reviewFilter === 'missing'
              ? []
              : viewableDocuments;
      setViewerDocumentIds(documentsForViewer.map(({ id }) => id));
      selectViewerDocument(document);
    },
    [
      conformToValidate,
      displayDocuments,
      flaggedToValidate,
      needsAttention,
      reviewFilter,
      selectViewerDocument,
      unclassifiedDocumentIds,
      usesReviewDesign,
      viewableDocuments,
    ],
  );
  const closeDocumentViewer = useCallback(() => {
    closeViewer();
    setViewerDocumentIds([]);
  }, [closeViewer]);
  const handleViewDocument: (document: DocumentViewerDocument) => void = openDocumentViewer;

  const renderBadges = useCallback(
    (doc: DocumentViewerDocument) => {
      const reviewStatus = reviewByDocumentId.get(doc.id)?.status;
      const processingStatus =
        reviewStatus === 'processing' ? (
          <DocumentProcessingStatus
            onOpen={doc.signedUrl ? () => openDocumentViewer(doc) : undefined}
          />
        ) : null;

      const conformity = visibleConformityByDocumentId.get(doc.id);
      if (displayContext === 'policyholder') {
        return (
          <>
            {processingStatus}
            {conformity && <ConformityReadOnlyBadge conformity={conformity} />}
          </>
        );
      }

      if (conformity && conformity.decision !== 'PENDING') {
        return <ConformityReadOnlyBadge conformity={conformity} />;
      }

      if (processingStatus) return processingStatus;

      if (reviewStatus === 'checked') {
        return (
          <Badge variant="light" tone="success" className="gap-1">
            <CheckCircle2Icon className="h-3.5 w-3.5" />
            {t('documentReview.status.checked')}
          </Badge>
        );
      }

      if (reviewStatus !== 'attention') return null;

      return (
        <Badge variant="light" tone="warning" className="gap-1">
          <AlertTriangleIcon className="h-3.5 w-3.5" />
          {t('documentReview.status.attention')}
        </Badge>
      );
    },
    [displayContext, openDocumentViewer, reviewByDocumentId, t, visibleConformityByDocumentId],
  );

  const renderBelowRow = useCallback(
    (doc: { id: string }) => {
      const conformity = visibleConformityByDocumentId.get(doc.id);
      if (conformity?.decision !== 'REJECTED') return null;
      if (displayContext === 'policyholder') {
        return (
          <ConformityReuploadZone
            documentId={doc.id}
            reason={conformity.reason}
            onReupload={onReuploadDocumentConformity}
          />
        );
      }
      if (!conformity.reason) return null;
      return (
        <Text size="xs" tone="destructive" className="mx-3 mb-2.5 flex items-start gap-1.5">
          <AlertCircleIcon aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <Text inline weight="semibold">
              {t('documentsListsPolicy.conformity.rejectionReasonPrefix')}
            </Text>{' '}
            {conformity.reason}
          </span>
        </Text>
      );
    },
    [displayContext, onReuploadDocumentConformity, t, visibleConformityByDocumentId],
  );

  const acceptAllControl = canDecideConformity &&
    isDocumentReviewEnabled &&
    !hideAcceptAllButton &&
    isManualVerdict && (
      <ConformityAcceptAllControl
        conformityByDocumentId={conformityByDocumentId}
        summary={categoryConformitySummary}
        conformToAccept={conformToAccept}
        onAcceptAll={onAcceptAllDocumentConformity}
      />
    );
  const uploadOptions: UploadOptions | undefined = readOnly
    ? undefined
    : {
        onDrop: uploadQueue.addFiles,
        multiple: allowMultiple,
        minPages,
        isUploading: uploadQueue.isUploading,
        authorizedFormats,
        enforceAiSizeLimit: aiClassificationDisplayConfig?.enabled,
      };
  const uploadQueueStatus = (
    <DocumentUploadQueue
      attempts={uploadQueue.attempts}
      receivedDocumentIds={new Set(viewableDocuments.map(({ id }) => id))}
      onRetry={uploadQueue.retry}
      onRemove={uploadQueue.remove}
      isUploading={uploadQueue.isUploading}
    />
  );
  const categoryLabel = (category: string) =>
    category
      ? t(`documentReview.category.${category}`, { defaultValue: t(`documents.${category}`) })
      : t('fileInput.uncategorizedFolderTitle');
  const documentTitle = (document: DocumentViewerDocument) => {
    const label = categoryLabel(document.category ?? '');
    const side = parseAIClassificationMetadata(document.metadata.aiclassification)?.side;
    return side === 'recto' || side === 'verso'
      ? `${label} · ${t(`aiClassification.sides.${side}`)}`
      : label;
  };

  return (
    <>
      <Card>
        <CardHeader className="p-4 md:p-6">
          <Stack gap={2} className="md:flex-row md:justify-between md:items-start">
            <div>
              <CardTitle className="text-lg md:text-xl">{cardTitle}</CardTitle>
              {subtitle && (
                <div className="text-xs md:text-sm text-muted-foreground font-inter mt-2">
                  {subtitle}
                </div>
              )}
            </div>
            <Group gap={2}>
              {(!usesReviewDesign || conformToAccept === undefined) && acceptAllControl}
              {usesReviewDesign && uploadOptions && (
                <UploadButton
                  upload={uploadOptions}
                  label={t('documentReview.uploadFiles')}
                  variant="outline"
                  icon={<UploadIcon className="h-4 w-4" />}
                />
              )}
              {displayContext === 'broker' && extraActions}
            </Group>
          </Stack>
        </CardHeader>
        <Stack asChild>
          <CardContent className="p-4 md:p-6 md:pt-0">
            {canDecideConformity && verdicts && verdicts.summary.required > 0 && (
              <DocumentFileSummary
                summary={verdicts.summary}
                categoryLabel={categoryLabel}
                onFilterChange={setReviewFilter}
              />
            )}
            {hasReviewLayout && (
              <DocumentReviewFilters
                summary={reviewSummary}
                filter={reviewFilter}
                onFilterChange={setReviewFilter}
                showAttention={displayContext === 'broker'}
                reviewDesign={usesReviewDesign}
              />
            )}
            {documentsLists.length === 0 && <NoResultsRow />}
            {documentsLists.length > 0 && usesReviewDesign && reviewFilter === 'attention' ? (
              <>
                {uploadQueueStatus}
                {reviewSummary.attentionCount === 0 && (
                  <Text size="sm" tone="muted">
                    {t('documentReview.noToValidate')}
                  </Text>
                )}
                <ToValidateList
                  flagged={flaggedToValidate}
                  conform={conformToValidate}
                  processingCount={
                    undecidedDocuments.length - flaggedToValidate.length - conformToValidate.length
                  }
                  acceptAll={
                    conformToAccept !== undefined &&
                    acceptAllControl && (
                      <ConformityAcceptAllControl
                        conformityByDocumentId={conformityByDocumentId}
                        summary={categoryConformitySummary}
                        conformToAccept={conformToAccept}
                        onAcceptAll={onAcceptAllDocumentConformity}
                        variant="outline"
                      />
                    )
                  }
                  getTitle={documentTitle}
                  renderBadges={renderBadges}
                  onView={handleViewDocument}
                />
              </>
            ) : documentsLists.length > 0 && usesReviewDesign && reviewFilter === 'missing' ? (
              <>
                {uploadQueueStatus}
                {missingRequiredCategories.length === 0 && (
                  <Text size="sm" tone="muted">
                    {t('documentReview.noMissing')}
                  </Text>
                )}
                <MissingList
                  categories={missingRequiredCategories.map(({ category, documentType }) => ({
                    category,
                    label: categoryLabel(category),
                    status: categoryStatus(
                      viewableDocuments.filter(
                        document =>
                          document.category === category ||
                          (documentType && document.category === documentType),
                      ),
                    ),
                  }))}
                  renderAddButton={
                    uploadOptions &&
                    (() => (
                      <UploadButton
                        upload={uploadOptions}
                        label={t('documentReview.add')}
                        variant="ghost"
                        icon={<PlusIcon className="h-4 w-4" />}
                      />
                    ))
                  }
                />
              </>
            ) : (
              documentsLists.length > 0 && (
                <DocumentsCollection
                  groups={getUploadedDocumentGroups({
                    documents: hasReviewLayout ? filteredDocuments : displayDocuments,
                    categories: hasReviewLayout ? filteredCategories : sortedDocumentsLists,
                    translate: t,
                  }).sort((left, right) =>
                    hasReviewLayout
                      ? Number(right.documents.length > 0) - Number(left.documents.length > 0)
                      : 0,
                  )}
                  emptyCategories={emptyCategories}
                  variant={usesReviewDesign ? 'review' : 'default'}
                  renderGroupStatus={
                    usesReviewDesign
                      ? (group: DocumentGroup<DocumentViewerDocument>) =>
                          categoryStatus(group.documents)
                      : undefined
                  }
                  classificationCategories={
                    readOnly || usesReviewDesign ? undefined : documentsLists
                  }
                  upload={
                    usesReviewDesign || !uploadOptions
                      ? undefined
                      : {
                          ...uploadOptions,
                          helpText: t(
                            aiClassificationDisplayConfig?.enabled
                              ? 'fileInput.aiMaxFileSize'
                              : 'fileInput.uploadMaxFileSize',
                          ),
                        }
                  }
                  onView={handleViewDocument}
                  onDelete={
                    canDelete
                      ? doc =>
                          deleteDocument({
                            policyId,
                            params: { ids: [doc.id], branchId },
                          })
                      : undefined
                  }
                  renderBadges={renderBadges}
                  renderBelowRow={renderBelowRow}
                  afterUpload={
                    <>
                      {hasReviewLayout &&
                        reviewFilter === 'attention' &&
                        reviewSummary.attentionCount === 0 && (
                          <Text size="sm" tone="muted">
                            {t('documentReview.noAttention')}
                          </Text>
                        )}
                      {hasReviewLayout &&
                        reviewFilter === 'missing' &&
                        missingRequiredCategories.length === 0 && (
                          <Text size="sm" tone="muted">
                            {t('documentReview.noMissing')}
                          </Text>
                        )}
                      {uploadQueueStatus}
                      {showUnclassifiedDocuments && unclassifiedDocs.length > 0 ? (
                        <TimedOutDocsSection
                          docs={unclassifiedDocs}
                          categories={sortedDocumentsLists.map(({ category, required }) => ({
                            category,
                            required,
                          }))}
                          onRetryAll={handleRetryTimedOutClassifications}
                          isRetrying={isClassifying}
                          onView={doc => {
                            if (doc.signedUrl) {
                              handleViewDocument({
                                id: doc.id,
                                name: doc.name,
                                signedUrl: doc.signedUrl,
                                createdAt: doc.createdAt,
                                type: doc.type,
                                category: doc.category,
                                metadata: doc.metadata as Record<
                                  string,
                                  string | number | boolean | undefined
                                >,
                              });
                            }
                          }}
                          onDelete={
                            canDelete
                              ? document =>
                                  deleteDocument({
                                    policyId,
                                    params: { ids: [document.id], branchId },
                                  })
                              : undefined
                          }
                          isBroker={displayContext === 'broker'}
                        />
                      ) : null}
                    </>
                  }
                />
              )
            )}
          </CardContent>
        </Stack>
      </Card>

      <DocumentViewer
        productId={policy.productId}
        review={
          selectedReview && isSelectedDecisionPending
            ? { ...selectedReview, reasons: [...selectedReview.reasons, 'conformity'] }
            : selectedReview
        }
        onRetryAnalysis={
          displayContext === 'broker' &&
          !readOnly &&
          aiClassificationDisplayConfig?.enabled &&
          selectedDocument &&
          (selectedDocument.type === 'supporting_document' ||
            selectedDocument.type === 'non_delegated') &&
          selectedDocument.metadata.scanresult !== 'INFECTED'
            ? documentId => void processClassificationDocuments([documentId], { force: true })
            : undefined
        }
        isRetryingAnalysis={isClassifying}
        displayContext={displayContext}
        document={selectedDocument}
        documents={viewerDocuments}
        onClose={closeDocumentViewer}
        onDocumentChange={selectViewerDocument}
        onDelete={handleDeleteDocument}
        documentCustomCategorieMappings={readOnly ? undefined : documentsLists}
        uploadMode={uploadMode}
        extraction={selectedVerdict?.extraction ?? null}
        extractionEnabled={Boolean(selectedVerdict?.extraction)}
        onExtractionFeedback={selectedVerdict?.extraction ? handleExtractionFeedback : undefined}
        isExtractionFeedbackPending={isFeedbackPending}
        validity={(selectedVerdict?.validity as AIValidityResult | undefined) ?? null}
        validityEnabled={Boolean(selectedVerdict?.validity)}
        onValidityOverride={
          displayContext === 'broker' && selectedVerdict?.validity && canOverrideValidity
            ? handleValidityOverride
            : undefined
        }
        isValidityOverriding={isValidityOverriding}
        coherence={selectedVerdict?.coherence}
        fraud={canViewFraud ? selectedDocumentFraud : undefined}
        corrections={selectedVerdict?.corrections}
        fraudPending={canViewFraud && selectedDocumentStream?.status === 'checking_fraud'}
        onCorrectFraudVerdict={canReviewFraud ? handleCorrectFraudVerdict : undefined}
        isCorrectingFraudVerdict={isCorrectingFraudVerdict}
        conformity={
          selectedDocument ? visibleConformityByDocumentId.get(selectedDocument.id) : undefined
        }
        onAcceptConformity={canDecideConformity ? onAcceptDocumentConformity : undefined}
        onRejectConformity={canDecideConformity ? onRejectDocumentConformity : undefined}
        onCancelConformity={canDecideConformity ? onCancelDocumentConformity : undefined}
      />
    </>
  );
}

export function DocumentsUploadPolicyModule(props: DocumentsUploadPolicyModuleProps) {
  const documentReviewEnabled = useFeatureFlag('feat_document_review');
  return documentReviewEnabled ? (
    <DocumentsUploadPolicyModuleV2 {...props} />
  ) : (
    <LegacyDocumentsUploadPolicyModule {...props} />
  );
}
